import { useEffect, useRef, useState } from "react";
import { Button } from "../ui/buttons/button";
import { Loader2, Printer } from "../ui/icons";
import { PrintContainer } from "../PrintContainer";
import { ReportDocument } from "./ReportDocument";
import type { ReportData } from "../ReportComponent/model/types";

export type { ReportData } from "../ReportComponent/model/types";

interface ReportPreviewModalProps {
  isOpen: boolean;
  reportData: ReportData | null;
  onClose: () => void;
}

export function ReportPreviewModal({
  isOpen,
  reportData,
  onClose,
}: ReportPreviewModalProps) {
  const [isPrinting, setIsPrinting] = useState(false);
  const isPrintingRef = useRef(false);

  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [isOpen, onClose]);

  // Os gráficos já são SVG estático, então o print não precisa rasterizar
  // nada: só montar o documento no #print-container e chamar a impressão.
  const handlePrint = () => {
    if (isPrintingRef.current) return;
    isPrintingRef.current = true;
    setIsPrinting(true);
    setTimeout(() => {
      const cleanup = () => {
        isPrintingRef.current = false;
        setIsPrinting(false);
        window.removeEventListener("afterprint", cleanup);
      };
      window.addEventListener("afterprint", cleanup);
      window.print();
    }, 200);
  };

  if (!isOpen || !reportData) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-modal-overlay bg-black bg-opacity-50 no-print"
        onClick={onClose}
      />

      <div className="fixed inset-0 z-modal flex items-center justify-center p-4 pointer-events-none print-modal-wrapper">
        <div className="bg-white rounded-lg shadow-2xl pointer-events-auto print-only-container w-[90%] max-w-[1200px] h-[90vh] flex flex-col">
          <div className="print-toolbar no-print bg-white border-b border-gray-300 px-6 py-4 flex justify-between items-center rounded-t-lg flex-shrink-0">
            <h2 className="text-lg font-semibold text-gray-800">
              Visualização do Relatório
            </h2>

            <div className="flex items-center gap-3">
              <Button
                onClick={handlePrint}
                disabled={isPrinting}
                className="px-6 py-2.5 h-auto bg-emerald-700 text-white hover:bg-emerald-800 font-medium shadow-sm"
              >
                {isPrinting ? (
                  <>
                    <Loader2 size={20} className="animate-spin" />
                    Imprimindo...
                  </>
                ) : (
                  <>
                    <Printer size={20} />
                    Imprimir / Salvar PDF
                  </>
                )}
              </Button>

              <button
                onClick={onClose}
                className="px-6 py-2.5 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors font-medium"
              >
                Fechar
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto bg-white p-6">
            <div className="max-w-[21cm] bg-white mx-auto shadow-[0_0_20px_rgba(0,0,0,0.1)]">
              <div className="py-[2cm] px-[1.5cm] bg-white box-border">
                <ReportDocument data={reportData} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <PrintContainer reportData={reportData} isActive={isPrinting} />
    </>
  );
}
